import { createContext, Dispatch, ReactNode, SetStateAction, useCallback, useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { PagesPaginateModel } from "../../models/Paginate/PagesPaginate";
import { ToastSettings } from "../../utils/ToastSettings";
import { TagsService } from "../../services/Tags/TagsService";
import { TagModel } from "../../models/Tags/TagModel";

interface ContextProps {
    tag: any,
    setTag: Dispatch<SetStateAction<any>>,
    tags: any[],
    tagsOptions: any[],
    params: any,
    setParams: Dispatch<SetStateAction<any>>,
    pages: PagesPaginateModel,
    setPages: Dispatch<SetStateAction<PagesPaginateModel>>,
    isLoading: boolean,
    error: string,
    setError: Dispatch<SetStateAction<string>>,
    handleList: () => void,
    handleFetch: (data: any) => Promise<boolean>,
}

export const TagContext = createContext<ContextProps>({} as ContextProps);

interface TagProviderProps {
    children: ReactNode;
}

export const TagProvider = ({ children }: TagProviderProps) => {
    const [tag, setTag] = useState<any>({} as any);
    const [tags, setTags] = useState<any[]>([]);
    const [tagsOptions, setTagsOptions] = useState<any | null>(null);
    const [params, setParams] = useState<any>(null);
    const [pages, setPages] = useState<PagesPaginateModel>({});
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<string>('');

    const service = new TagsService();
    const location = useLocation();

    const handleList = useCallback(async () => {
        if (!params || !location.pathname.includes('/articles/list')) {
            return false;
        }

        try {
            setIsLoading(true);
            const [_Response, _Error] = await service.list(params);
            setIsLoading(false);

            if (!!_Error) {
                return setError(_Response?.message || _Error);
            }

            setTags(_Response?.items);
            setPages(_Response?.pagination);
            return setError('');
        } catch (e) {
            setIsLoading(false);
            setError('Não foi possível carregar as tags do sistema.');
        }
    }, [params, location.pathname]);

    const handleFetch = async (data: any) => {
        try {
            const [_Response, _Error, _Code, _Errors] = await service.fetch(data);
            if (!!_Error) {
                ToastSettings(_Response?.message || _Error, 'bottom-center', 'error', () => { }, _Errors);
                return false;
            }

            setError('');
            return true;
        } catch (e) {
            setError('Houve um erro ao salvar a tag');
            return false;
        }
    }

    useEffect(() => {
        !!error && toast.error(error);
    }, [error]);

    useEffect(() => {
        handleList().then();
    }, [handleList]);

    useEffect(() => {
        setTagsOptions(
          tags?.map((item: TagModel) => ({
            label: item?.content,
            value: item.id,
          }))
        );
      }, [tags]);

    return (
        <TagContext.Provider value={{
            tag,
            setTag,
            tags,
            tagsOptions,
            params,
            setParams,
            pages,
            setPages,
            isLoading,
            error,
            setError,
            handleList,
            handleFetch,
        }}>
            {children}
        </TagContext.Provider>
    );
}

export const useTag = () => useContext(TagContext);